<script setup>
// Inspector do nó "Ligação (FlexCalls)". O assistente de voz é VINCULADO pela
// Navi no FlexCalls (voz, número e modelo ficam lá — decisão Ivan 02/09/2026);
// aqui o operador escolhe qual assistente vinculado o agente usa, ajusta
// saudação e duração, vê o roteiro que vai para o FlexCalls, sincroniza e
// homologa ligando para um telefone de verdade. Colocar o nó no fluxo NÃO
// dispara ligação nenhuma: quem disca é o botão "Ligar com a IA" da conversa
// (has_flexcalls_call_node) ou o teste daqui.
//
// agentId não mora nos dados do nó (o nó só guarda configuração do fluxo) —
// por isso ele chega como prop separada, vinda de route.params no canvas pai.
//
// flexcallsConfig chega de fora: é agentRecord.config.flexcalls, gravado a
// cada sync automático. Sem lê-lo, uma falha silenciosa de sync no save fica
// invisível — o operador reabre o nó e vê um botão limpo.
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import agentsAPI from 'dashboard/api/agents/agents';
import flexcallsAPI from 'dashboard/api/agents/flexcalls';

const props = defineProps({
  data: { type: Object, required: true },
  agentId: { type: [Number, String], required: true },
  flexcallsConfig: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();
const store = useStore();
const route = useRoute();

const up = (key, value) => emit('update', { [key]: value });

// Os dois modos funcionam. "both" faz o agente também ATENDER quem ligar para
// o número escolhido — por isso a caixa vira obrigatória: é onde a conversa da
// ligação recebida nasce.
const MODES = [
  {
    value: 'outbound',
    icon: 'i-lucide-phone-outgoing',
    labelKey: 'MODE_OUTBOUND',
    descKey: 'MODE_OUTBOUND_DESC',
  },
  {
    value: 'both',
    icon: 'i-lucide-phone-call',
    labelKey: 'MODE_BOTH',
    descKey: 'MODE_BOTH_DESC',
  },
];

const mode = computed(() => props.data.mode || 'outbound');
const inboxRequired = computed(() => mode.value === 'both');
// store pode não existir (ex.: montagem isolada em teste) — não deixa a tela
// quebrar por causa de um <select> que só aparece condicionalmente.
const inboxes = computed(() => store?.getters?.['inboxes/getInboxes'] || []);
const accountId = computed(() => route?.params?.accountId);

const onModeChange = value => {
  const patch = { mode: value };
  // Sair do modo "both" torna a caixa irrelevante; não deixar um valor velho
  // reaparecer escondido se o usuário voltar para "both" depois.
  if (value !== 'both') patch.inboxId = null;
  emit('update', patch);
};

const greeting = computed(() => props.data.greeting || '');
const greetingLength = computed(() => greeting.value.length);
const maxDuration = computed(() => props.data.maxDuration || 600);

const onMaxDurationInput = event => {
  const value = Number(event.target.value);
  up('maxDuration', Number.isFinite(value) ? value : 600);
};

// ░░ Catálogos ░░
// Falha de carga NUNCA apaga o valor salvo: só as <option>s vêm do catálogo.
const useCatalog = fetcher => {
  const state = reactive({ items: [], loading: false, error: '' });
  const load = async () => {
    state.loading = true;
    state.error = '';
    try {
      const { data: result } = await fetcher();
      const list = Array.isArray(result) ? result : result?.data;
      state.items = Array.isArray(list) ? list : [];
    } catch (e) {
      state.error =
        e?.response?.data?.error ||
        t('AI_AGENTS.FLOW.FLEXCALLS.CATALOG_LOAD_FAILED');
    } finally {
      state.loading = false;
    }
  };
  return { state, load };
};

// Assistentes vinculados pela Navi a ESTA conta (voz/número/modelo já vêm
// prontos do FlexCalls).
const assistantCatalog = useCatalog(() => flexcallsAPI.assistants());
// Números do pool da Navi atribuídos a esta conta (roteamento do receber).
const numberCatalog = useCatalog(() => flexcallsAPI.numbers());

const numberLabel = number =>
  number.nickname ? `${number.e164} — ${number.nickname}` : number.e164;

// Um assistente já em uso por OUTRO agente aparece desabilitado — vincular o
// mesmo assistente a dois agentes bagunça pra quem recebe a ligação.
const currentAgentId = computed(() => Number(props.agentId));
const assistantLabel = a => `${a.name} · ${a.phone}`;
const usedByOther = a =>
  Boolean(a.agent_id) && Number(a.agent_id) !== currentAgentId.value;
const selectedAssistant = computed(() =>
  assistantCatalog.state.items.find(
    a => String(a.id) === String(props.data.flexcallsAssistantId || '')
  )
);
const onAssistantChange = event => {
  const value = event.target.value;
  up('flexcallsAssistantId', value ? Number(value) : null);
};
const assistantReady = computed(
  () => Boolean(selectedAssistant.value) && selectedAssistant.value.webhook_ok
);

// ░░ Roteiro (prompt no formato do FlexCalls) ░░
const syncing = ref(false);
// Estado inicial: config antigo do agente até o roteiro carregar; com
// assistente vinculado, loadPrompt() troca pelo estado do assistente.
const syncError = ref(
  props.data.flexcallsAssistantId ? '' : props.flexcallsConfig?.last_error || ''
);
const syncedAt = ref(props.flexcallsConfig?.synced_at || '');
const promptOpen = ref(false);
const prompt = reactive({
  loading: false,
  error: '',
  systemPrompt: '',
  initialMessage: '',
  loaded: false,
});
const loadPrompt = async () => {
  prompt.loading = true;
  prompt.error = '';
  try {
    const { data: result } = await agentsAPI.flexcallsPrompt(props.agentId);
    prompt.systemPrompt = result?.system_prompt || '';
    prompt.initialMessage = result?.initial_message || '';
    prompt.loaded = true;
    // Assistente vinculado: o estado da sincronização vive nele, não no
    // config antigo do agente (que guardava o erro do sync de voz/modelo).
    if (result?.assistant) {
      syncedAt.value = result.assistant.prompt_synced_at || '';
      syncError.value = result.assistant.last_error || '';
    }
  } catch (e) {
    prompt.error =
      e?.response?.data?.error ||
      t('AI_AGENTS.FLOW.FLEXCALLS.PROMPT_LOAD_FAILED');
  } finally {
    prompt.loading = false;
  }
};
const togglePrompt = () => {
  promptOpen.value = !promptOpen.value;
  if (promptOpen.value && !prompt.loaded && !prompt.loading) loadPrompt();
};

const formatTime = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};
const formattedSyncedAt = computed(() => formatTime(syncedAt.value));

const syncAssistant = async () => {
  syncing.value = true;
  syncError.value = '';
  try {
    const { data: result } = await agentsAPI.flexcallsSync(
      props.agentId,
      props.data.flexcallsAssistantId
    );
    syncedAt.value = result?.synced_at || new Date().toISOString();
    // O roteiro mostrado pode ter mudado com o save: recarrega se estava aberto.
    if (promptOpen.value) loadPrompt();
  } catch (error) {
    syncError.value =
      error?.response?.data?.error || t('AI_AGENTS.FLOW.FLEXCALLS.SYNC_FAILED');
  } finally {
    syncing.value = false;
  }
};

// ░░ Homologação por telefone ░░
const testPhone = ref('');
const testing = ref(false);
const testError = ref('');
const testConversationId = ref(null);
const testDigits = computed(() => testPhone.value.replace(/\D/g, ''));
const phoneValid = computed(() => testDigits.value.length >= 10);
const canTest = computed(
  () => assistantReady.value && phoneValid.value && !testing.value
);
const testConversationPath = computed(() =>
  testConversationId.value && accountId.value
    ? `/app/accounts/${accountId.value}/conversations/${testConversationId.value}`
    : ''
);

const placeTestCall = async () => {
  if (!canTest.value) return;
  testing.value = true;
  testError.value = '';
  testConversationId.value = null;
  try {
    const { data: result } = await agentsAPI.flexcallsTestCall(
      props.agentId,
      testPhone.value.trim(),
      props.data.flexcallsAssistantId
    );
    testConversationId.value = result?.conversation_id || null;
  } catch (error) {
    testError.value =
      error?.response?.data?.error ||
      t('AI_AGENTS.FLOW.FLEXCALLS.TEST_CALL_FAILED');
  } finally {
    testing.value = false;
  }
};

onMounted(() => {
  if (props.data.flexcallsAssistantId) loadPrompt();
  assistantCatalog.load();
  numberCatalog.load();
});
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Cabeçalho -->
    <div
      class="flex items-center justify-between border-b border-n-weak px-4 py-3"
    >
      <div class="flex items-center gap-2">
        <span
          class="grid size-7 place-items-center rounded-lg bg-n-brand/15 text-n-brand"
        >
          <span class="i-lucide-phone-call text-base" />
        </span>
        <div>
          <h3 class="text-sm font-semibold leading-tight text-n-slate-12">
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.TITLE') }}
          </h3>
          <p class="text-[11px] leading-tight text-n-slate-10">
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.DESCRIPTION') }}
          </p>
        </div>
      </div>
      <button
        class="i-lucide-x text-n-slate-10 hover:text-n-slate-12"
        :aria-label="t('AI_AGENTS.FLOW.FLEXCALLS.CLOSE')"
        @click="emit('close')"
      />
    </div>

    <div class="flex-1 space-y-3 overflow-y-auto p-4">
      <!-- ░░ Assistente vinculado ░░ -->
      <section class="rounded-xl border border-n-weak p-3">
        <div class="mb-3 flex items-center gap-1.5">
          <span class="i-lucide-bot text-[13px] text-n-slate-10" />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.SEC_ASSISTANT') }}
          </span>
        </div>

        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.ASSISTANT') }}
        </label>
        <select
          data-testid="flexcalls-assistant"
          class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
          :value="
            data.flexcallsAssistantId ? String(data.flexcallsAssistantId) : ''
          "
          @change="onAssistantChange"
        >
          <option value="">
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.ASSISTANT_PICK') }}
          </option>
          <option
            v-for="a in assistantCatalog.state.items"
            :key="a.id"
            :value="String(a.id)"
            :disabled="usedByOther(a)"
          >
            {{ assistantLabel(a)
            }}{{
              usedByOther(a)
                ? ` — ${t('AI_AGENTS.FLOW.FLEXCALLS.ASSISTANT_IN_USE', {
                    agent: a.agent_name,
                  })}`
                : ''
            }}
          </option>
        </select>
        <p
          v-if="
            !assistantCatalog.state.loading &&
            !assistantCatalog.state.items.length
          "
          data-testid="flexcalls-assistant-none"
          class="mt-1.5 flex items-start gap-1.5 text-xs text-n-amber-11"
        >
          <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.ASSISTANT_NONE') }}
        </p>
        <p
          v-else-if="assistantCatalog.state.error"
          class="mt-1.5 text-xs text-n-ruby-11"
        >
          {{ assistantCatalog.state.error }}
        </p>

        <div
          v-if="selectedAssistant"
          data-testid="flexcalls-assistant-card"
          class="mt-2 rounded-lg border border-n-weak bg-n-solid-2 p-3 text-xs text-n-slate-11"
        >
          <div class="flex items-center justify-between gap-2">
            <span class="font-medium text-n-slate-12">
              {{ selectedAssistant.name }}
            </span>
            <a
              v-if="selectedAssistant.widget_url"
              data-testid="flexcalls-assistant-test"
              :href="selectedAssistant.widget_url"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-1 text-n-brand hover:underline"
            >
              <span class="i-lucide-play" />
              {{ t('AI_AGENTS.FLOW.FLEXCALLS.ASSISTANT_TEST_BROWSER') }}
            </a>
          </div>
          <p class="mt-1">
            {{
              t('AI_AGENTS.FLOW.FLEXCALLS.ASSISTANT_PHONE', {
                phone: selectedAssistant.phone,
              })
            }}
          </p>
          <p
            v-if="selectedAssistant.webhook_ok"
            class="mt-1 flex items-center gap-1 text-n-teal-11"
          >
            <span class="i-lucide-check-circle-2" />
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.WEBHOOK_ON') }}
          </p>
          <p
            v-else
            data-testid="flexcalls-webhook-off"
            class="mt-1 flex items-start gap-1 text-n-amber-11"
          >
            <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
            {{
              selectedAssistant.last_error ||
              t('AI_AGENTS.FLOW.FLEXCALLS.WEBHOOK_OFF')
            }}
          </p>
        </div>
        <p class="mt-2 text-[11px] text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.ASSISTANT_HINT') }}
        </p>
      </section>

      <!-- ░░ Modo & caixa ░░ -->
      <section class="rounded-xl border border-n-weak p-3">
        <div class="mb-3 flex items-center gap-1.5">
          <span class="i-lucide-phone text-[13px] text-n-slate-10" />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.SEC_MODE') }}
          </span>
        </div>

        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.MODE') }}
        </label>
        <div class="mb-3 space-y-1.5">
          <button
            v-for="m in MODES"
            :key="m.value"
            type="button"
            data-testid="flexcalls-mode"
            class="flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-all"
            :class="[
              mode === m.value
                ? 'border-n-brand bg-n-brand/10'
                : 'border-n-weak hover:border-n-slate-8',
            ]"
            @click="onModeChange(m.value)"
          >
            <span
              class="text-base"
              :class="[
                m.icon,
                mode === m.value ? 'text-n-brand' : 'text-n-slate-10',
              ]"
            />
            <span class="flex-1">
              <span class="flex items-center gap-1.5">
                <span class="block text-xs font-medium text-n-slate-12">
                  {{ t(`AI_AGENTS.FLOW.FLEXCALLS.${m.labelKey}`) }}
                </span>
              </span>
              <span class="block text-[10px] text-n-slate-10">
                {{ t(`AI_AGENTS.FLOW.FLEXCALLS.${m.descKey}`) }}
              </span>
            </span>
            <span
              v-if="mode === m.value"
              class="i-lucide-check text-sm text-n-brand"
            />
          </button>
        </div>

        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.NUMBER') }}
        </label>
        <p class="mb-1.5 text-[10px] text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.NUMBER_HINT') }}
        </p>
        <select
          data-testid="flexcalls-number"
          class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
          :value="data.numberId || ''"
          @change="up('numberId', Number($event.target.value) || null)"
        >
          <option value="">
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.NUMBER_PICK') }}
          </option>
          <option
            v-for="number in numberCatalog.state.items"
            :key="number.id"
            :value="number.id"
          >
            {{ numberLabel(number) }}
          </option>
        </select>
        <p
          v-if="
            !numberCatalog.state.loading && !numberCatalog.state.items.length
          "
          data-testid="flexcalls-no-number"
          class="mb-3 mt-1 flex items-start gap-1.5 text-xs text-n-amber-11"
        >
          <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.NO_NUMBER') }}
        </p>
        <div v-else class="mb-3" />

        <template v-if="inboxRequired">
          <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.INBOX') }}
          </label>
          <select
            data-testid="flexcalls-inbox"
            class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
            :value="data.inboxId || ''"
            @change="up('inboxId', Number($event.target.value) || null)"
          >
            <option value="">
              {{ t('AI_AGENTS.FLOW.FLEXCALLS.INBOX_PICK') }}
            </option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
          <p
            v-if="!data.inboxId"
            class="mt-1 flex items-start gap-1.5 text-xs text-n-amber-11"
          >
            <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.INBOX_HINT') }}
          </p>
        </template>
      </section>

      <!-- ░░ Mensagem ░░ -->
      <section class="rounded-xl border border-n-weak p-3">
        <div class="mb-3 flex items-center gap-1.5">
          <span class="i-lucide-message-square text-[13px] text-n-slate-10" />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.SEC_MESSAGE') }}
          </span>
        </div>

        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.GREETING') }}
        </label>
        <textarea
          rows="3"
          maxlength="200"
          :placeholder="t('AI_AGENTS.FLOW.FLEXCALLS.GREETING_PH')"
          class="mb-1 w-full rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
          :value="greeting"
          @input="up('greeting', $event.target.value)"
        />
        <p class="mb-3 text-right text-[10px] text-n-slate-10">
          {{
            t('AI_AGENTS.FLOW.FLEXCALLS.GREETING_COUNT', { n: greetingLength })
          }}
        </p>

        <div class="mb-1 flex items-center justify-between">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.MAX_DURATION') }}
          </label>
          <span class="text-xs font-semibold tabular-nums text-n-brand">
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.SECONDS', { n: maxDuration }) }}
          </span>
        </div>
        <input
          type="range"
          min="30"
          max="1800"
          step="10"
          :value="maxDuration"
          class="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-n-slate-5 accent-n-brand"
          @input="onMaxDurationInput"
        />
        <p class="mt-1 text-[10px] text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.MAX_DURATION_HINT') }}
        </p>
      </section>

      <!-- ░░ Roteiro enviado ao FlexCalls ░░ -->
      <section class="rounded-xl border border-n-weak p-3">
        <div class="mb-3 flex items-center gap-1.5">
          <span class="i-lucide-scroll-text text-[13px] text-n-slate-10" />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.SEC_PROMPT') }}
          </span>
        </div>
        <p class="mb-3 text-[11px] text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.PROMPT_HINT') }}
        </p>

        <button
          type="button"
          data-testid="flexcalls-prompt-toggle"
          class="flex w-full items-center justify-between rounded-lg border border-n-weak px-2.5 py-2 text-xs text-n-slate-11 transition-colors hover:bg-n-alpha-2"
          :aria-expanded="promptOpen"
          @click="togglePrompt"
        >
          <span class="flex items-center gap-1.5">
            <span class="i-lucide-eye" />
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.PROMPT_TOGGLE') }}
          </span>
          <span
            :class="
              promptOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
          />
        </button>

        <div v-if="promptOpen" class="mt-2">
          <p
            v-if="prompt.loading"
            class="flex items-center gap-1.5 text-xs text-n-slate-10"
          >
            <span class="i-lucide-loader-2 animate-spin" />
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.PROMPT_LOADING') }}
          </p>
          <p
            v-else-if="prompt.error"
            class="flex items-start gap-1.5 text-xs text-n-ruby-11"
          >
            <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
            {{ prompt.error }}
          </p>
          <div v-else data-testid="flexcalls-prompt-preview" class="space-y-2">
            <div>
              <p class="mb-1 text-[10px] text-n-slate-10">
                {{ t('AI_AGENTS.FLOW.FLEXCALLS.PROMPT_INITIAL') }}
              </p>
              <div
                class="max-h-24 overflow-auto whitespace-pre-wrap rounded-lg border border-n-weak bg-n-solid-2 p-2.5 font-mono text-[11px] leading-relaxed text-n-slate-12"
              >
                {{ prompt.initialMessage }}
              </div>
            </div>
            <div>
              <p class="mb-1 text-[10px] text-n-slate-10">
                {{ t('AI_AGENTS.FLOW.FLEXCALLS.PROMPT_SYSTEM') }}
              </p>
              <div
                class="max-h-72 overflow-auto whitespace-pre-wrap rounded-lg border border-n-weak bg-n-solid-2 p-2.5 font-mono text-[11px] leading-relaxed text-n-slate-12"
              >
                {{ prompt.systemPrompt }}
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          data-testid="flexcalls-sync-button"
          class="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-n-brand py-2 text-sm font-medium text-n-brand transition-colors hover:bg-n-brand/10 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="syncing"
          @click="syncAssistant"
        >
          <span
            :class="
              syncing ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-refresh-cw'
            "
          />
          {{
            syncing
              ? t('AI_AGENTS.FLOW.FLEXCALLS.SYNCING')
              : t('AI_AGENTS.FLOW.FLEXCALLS.SYNC')
          }}
        </button>
        <!-- syncedAt e syncError não são mutuamente exclusivos: sincronizou
             um dia e falhou no save mais recente — as duas informações valem. -->
        <p
          v-if="syncedAt"
          data-testid="flexcalls-sync-success"
          class="mt-2 flex items-center gap-1.5 text-xs text-n-teal-11"
        >
          <span class="i-lucide-check-circle-2" />
          {{
            t('AI_AGENTS.FLOW.FLEXCALLS.SYNCED_AT', {
              time: formattedSyncedAt,
            })
          }}
        </p>
        <p
          v-if="syncError"
          data-testid="flexcalls-sync-error"
          class="mt-2 flex items-start gap-1.5 text-xs text-n-ruby-11"
        >
          <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
          {{ syncError }}
        </p>
      </section>

      <!-- ░░ Homologação por telefone ░░ -->
      <section
        class="rounded-xl border border-n-brand/40 bg-n-brand/5 p-3"
        data-testid="flexcalls-homolog"
      >
        <div class="mb-3 flex items-center gap-1.5">
          <span class="i-lucide-phone-forwarded text-[13px] text-n-brand" />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-brand"
          >
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.SEC_HOMOLOG') }}
          </span>
        </div>
        <p class="mb-3 text-[11px] text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.HOMOLOG_HINT') }}
        </p>

        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.HOMOLOG_PHONE') }}
        </label>
        <div class="flex gap-2">
          <input
            v-model="testPhone"
            data-testid="flexcalls-test-phone"
            type="tel"
            inputmode="tel"
            autocomplete="off"
            :placeholder="t('AI_AGENTS.FLOW.FLEXCALLS.HOMOLOG_PHONE_PH')"
            class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-2 text-sm tabular-nums text-n-slate-12 focus:border-n-brand focus:outline-none"
            @keydown.enter.prevent="placeTestCall"
          />
          <button
            type="button"
            data-testid="flexcalls-test-call"
            class="flex shrink-0 items-center gap-1.5 rounded-lg bg-n-brand px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-n-brand/90 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!canTest"
            @click="placeTestCall"
          >
            <span
              :class="
                testing
                  ? 'i-lucide-loader-2 animate-spin'
                  : 'i-lucide-phone-outgoing'
              "
            />
            {{
              testing
                ? t('AI_AGENTS.FLOW.FLEXCALLS.HOMOLOG_CALLING')
                : t('AI_AGENTS.FLOW.FLEXCALLS.HOMOLOG_CALL')
            }}
          </button>
        </div>

        <p v-if="!selectedAssistant" class="mt-2 text-[11px] text-n-amber-11">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.HOMOLOG_NEEDS_ASSISTANT') }}
        </p>
        <p v-else-if="!assistantReady" class="mt-2 text-[11px] text-n-amber-11">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.HOMOLOG_NEEDS_WEBHOOK') }}
        </p>

        <p
          v-if="testConversationId"
          data-testid="flexcalls-test-success"
          class="mt-2 flex items-start gap-1.5 text-xs text-n-teal-11"
        >
          <span class="i-lucide-check-circle-2 mt-0.5 shrink-0" />
          <span>
            {{ t('AI_AGENTS.FLOW.FLEXCALLS.HOMOLOG_STARTED') }}
            <a
              v-if="testConversationPath"
              data-testid="flexcalls-test-link"
              :href="testConversationPath"
              target="_blank"
              rel="noopener noreferrer"
              class="font-medium underline"
            >
              {{ t('AI_AGENTS.FLOW.FLEXCALLS.HOMOLOG_OPEN_CONVERSATION') }}
            </a>
          </span>
        </p>
        <p
          v-if="testError"
          data-testid="flexcalls-test-error"
          class="mt-2 flex items-start gap-1.5 text-xs text-n-ruby-11"
        >
          <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
          {{ testError }}
        </p>
        <p class="mt-2 text-[10px] text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.FLEXCALLS.HOMOLOG_COST') }}
        </p>
      </section>
    </div>

    <!-- Remover -->
    <div class="border-t border-n-weak p-3">
      <button
        class="flex w-full items-center justify-center gap-1.5 rounded-lg border border-n-ruby-7 py-2 text-xs font-medium text-n-ruby-9 transition-colors hover:bg-n-ruby-3"
        @click="emit('remove')"
      >
        <span class="i-lucide-trash-2 text-sm" />
        {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
      </button>
    </div>
  </div>
</template>
